import { getStoreActions } from "../../../utils/session/storeStateUtils";
import useCommandFlowStore from "../../../store/commandFlowStore";

/**
 * Handles redirect command result when it has a prompt
 * @param {string|Promise<string>} prompt - The prompt to display
 * @param {Function} resolvePrompt - Function to resolve the prompt
 * @param {string} nextCommandName - Name of the redirect command
 * @param {Function} onStepStart - Callback for step start
 * @param {Function} [onCommandStart] - Callback for command start (fired before typing)
 */
export async function handleRedirectPrompt(
  prompt,
  resolvePrompt,
  nextCommandName,
  onStepStart,
  onCommandStart
) {
  const {
    addOutputWithTypingEffect,
    setCurrentPromptWithTypingEffect,
    setInSession,
  } = getStoreActions();

  // Fire onCommandStart immediately — same latency-sensitive contract as the
  // top-level handleStartNewCommand path. Verification engines react at the
  // moment of intent, not at the end of the typing animation. The redirected
  // command IS a new command from the consumer's perspective (e.g. an umbrella
  // resolved to a sub-command).
  if (onCommandStart && nextCommandName) {
    try {
      const sessionDataSnapshot =
        useCommandFlowStore.getState().sessionData ?? {};
      onCommandStart(nextCommandName, sessionDataSnapshot);
    } catch (err) {
      console.error("onCommandStart threw (redirect):", err);
    }
  }

  const resolvedPrompt = await resolvePrompt(prompt);

  // Check if prompt contains multiple lines and split them for delayed display
  if (typeof resolvedPrompt === "string" && resolvedPrompt.includes("\n")) {
    const lines = resolvedPrompt.split("\n");
    const lastLine = lines.pop(); // Remove the last line to use as prompt

    if (lines.length > 0) {
      // Join into a single output entry so one Enter skips the whole block.
      // CSS `white-space: pre-wrap` renders the embedded \n as line breaks.
      const outputLines = [{ type: "output", text: lines.join("\n") }];
      await addOutputWithTypingEffect(outputLines);
      // Re-set isLoading so the ">" doesn't flash before the prompt typing starts
      useCommandFlowStore.setState({ isLoading: true });
    }

    // Set only the last line as the current prompt with typing effect
    await setCurrentPromptWithTypingEffect(lastLine);
  } else {
    // Single-line prompt - use typing effect
    await setCurrentPromptWithTypingEffect(resolvedPrompt);
  }

  setInSession(true);

  if (onStepStart) {
    onStepStart(nextCommandName);
  }
}
